// src/pages/finance-portal/components/StripeLinkButton.js
// Stripe Financial Connections button - creates session and opens Stripe FC modal

import React, { useState, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Button, CircularProgress } from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import axiosInstance from '../../../utils/axiosInstance';
import { stripe_publishable_key } from '../../../config';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const stripePromise = stripe_publishable_key ? loadStripe(stripe_publishable_key) : Promise.resolve(null);

export default function StripeLinkButton({ onSuccess, onExit, buttonText = 'Connect Bank Account', variant = 'contained', fullWidth = false, disabled = false }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleConnect = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // 1. Create a Financial Connections session on the backend
            const response = await axiosInstance.post('/api/finance/stripe/create-session', {});
            if (!response.data.success) {
                setError(response.data.message || 'Failed to initialize bank connection');
                setLoading(false);
                return;
            }

            const { client_secret, session_id } = response.data;

            // 2. Load Stripe and open the Financial Connections modal
            const stripe = await stripePromise;
            if (!stripe) {
                setError('Failed to load Stripe');
                setLoading(false);
                return;
            }

            const result = await stripe.collectFinancialConnectionsAccounts({
                clientSecret: client_secret,
            });

            if (result.error) {
                setError(result.error.message || 'Connection was cancelled');
                if (onExit) {
                    onExit(result.error);
                }
                setLoading(false);
                return;
            }

            // 3. Connection successful - pass session_id and accounts to parent
            if (onSuccess) {
                onSuccess(session_id, {
                    accounts: result.financialConnectionsSession?.accounts || [],
                    institution: {
                        name: result.financialConnectionsSession?.accounts?.[0]?.institution_name || 'Bank',
                    },
                });
            }
        } catch (err) {
            const msg = err.response?.data?.message || err.message || 'Failed to connect bank';
            setError(msg);
            if (onExit) {
                onExit({ message: msg });
            }
        } finally {
            setLoading(false);
        }
    }, [onSuccess, onExit]);

    return (
        <Button
            variant={variant}
            onClick={handleConnect}
            disabled={disabled || loading}
            fullWidth={fullWidth}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <AccountBalanceIcon />}
            sx={{
                fontFamily,
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '12px',
                px: 3,
                py: 1.5,
                fontSize: '0.95rem',
                bgcolor: variant === 'contained' ? ACCENT : 'transparent',
                color: variant === 'contained' ? '#fff' : ACCENT,
                border: variant === 'outlined' ? `2px solid ${ACCENT}` : 'none',
                '&:hover': {
                    bgcolor: variant === 'contained' ? '#16a34a' : 'rgba(34, 197, 94, 0.08)',
                },
                '&:disabled': {
                    bgcolor: variant === 'contained' ? '#94a3b8' : 'transparent',
                    color: '#94a3b8',
                },
            }}
        >
            {loading ? 'Connecting...' : error ? 'Retry Connection' : buttonText}
        </Button>
    );
}
